/**
 * motion.js — preferências de movimento do visual Sphere.
 *
 * - `prefers-reduced-motion` do sistema é observado inclusive depois da montagem;
 * - o toggle "Animações" (menu da conta) é GLOBAL ao tema e separado de qualquer
 *   comando de domínio — pausar animações nunca pausa agentes no backend;
 * - `usePageVisible` / `useInViewport` param o render fora da viewport e com a
 *   aba oculta.
 * Todos os listeners/observers são removidos na desmontagem (StrictMode-safe).
 */
import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const MOTION_KEY = 'nava.motion';

function lerPreferencia() {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(MOTION_KEY) === 'off' ? false : true;
  } catch {
    return true;
  }
}

const MotionContext = createContext({ enabled: true, setEnabled: () => {} });

export function MotionProvider({ children }) {
  const [enabled, setEnabledState] = useState(lerPreferencia);
  const setEnabled = useCallback((v) => {
    setEnabledState(Boolean(v));
    try { localStorage.setItem(MOTION_KEY, v ? 'on' : 'off'); } catch { /* só em memória */ }
  }, []);
  const value = useMemo(() => ({ enabled, setEnabled }), [enabled, setEnabled]);
  return createElement(MotionContext.Provider, { value }, children);
}

/** Toggle global de animações ({enabled, setEnabled}). */
export function useMotionPreference() {
  return useContext(MotionContext);
}

/** true quando o sistema pede movimento reduzido (reage a mudanças). */
export function useReducedMotion() {
  const query = '(prefers-reduced-motion: reduce)';
  const [reduced, setReduced] = useState(() => (
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches : false
  ));
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(query);
    const on = () => setReduced(mq.matches);
    on();
    if (mq.addEventListener) mq.addEventListener('change', on);
    else if (mq.addListener) mq.addListener(on);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', on);
      else if (mq.removeListener) mq.removeListener(on);
    };
  }, []);
  return reduced;
}

/** false quando a aba está oculta (`document.hidden`). */
export function usePageVisible() {
  const [visible, setVisible] = useState(() => (typeof document === 'undefined' ? true : !document.hidden));
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const on = () => setVisible(!document.hidden);
    document.addEventListener('visibilitychange', on);
    return () => document.removeEventListener('visibilitychange', on);
  }, []);
  return visible;
}

/** true enquanto o elemento intersecta a viewport (sem IntersectionObserver → true). */
export function useInViewport(ref) {
  const [inView, setInView] = useState(true);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof window === 'undefined' || typeof window.IntersectionObserver !== 'function') return undefined;
    const io = new window.IntersectionObserver(([entry]) => setInView(Boolean(entry?.isIntersecting)));
    io.observe(el);
    return () => io.disconnect();
  }, [ref]);
  return inView;
}

/**
 * Movimento efetivo de uma região decorativa: preferência do usuário E sistema
 * sem reduced-motion E aba visível E região na viewport.
 */
export function useAnimationAllowed(ref) {
  const { enabled } = useMotionPreference();
  const reduced = useReducedMotion();
  const visible = usePageVisible();
  const inView = useInViewport(ref);
  return { allowed: enabled && !reduced && visible && inView, reduced, enabled };
}
